import { Type, Static } from '@sinclair/typebox'

import { CROP_TYPES } from '@ruminati/constants/gaf/grains'
import { NullableNumber, NullableOptionalNumber } from './utils/shared-types'


export const ReportResultsTB = Type.Object({
  totals: Type.Object({
    total: NullableNumber(),
      sequestration: NullableNumber(),
      net: NullableNumber()
  }),
  sequestration: Type.Object({
      trees: Type.Number(),
      soil: Type.Number(),
  }),
  byScopes: Type.Object({
      scope1: Type.Number(),
      scope2: Type.Number(),
      scope3: Type.Number(),
  }),
  scope1ByGas: Type.Object({
      carbonDioxide: Type.Number(),
      methane: Type.Number(),
      nitrousOxide: Type.Number()
  }),
  emissions: Type.Object({
      sheepBeef: NullableOptionalNumber(),
      grains:  NullableOptionalNumber(),
      shared:  NullableOptionalNumber(),
      feedlot:  NullableOptionalNumber(),
      processor:  NullableOptionalNumber(),
      dairy:  NullableOptionalNumber(),
  }),
  intensity: Type.Object({
      netPerDSE:  NullableOptionalNumber(),
      kgCo2PerKgBeefMeatSold: NullableOptionalNumber(),
      kgCo2PerKgGrainProduced: NullableOptionalNumber(),
      kgCo2PerKgGreasyWool: NullableOptionalNumber(),
      kgCo2PerKgSheepMeatSold: NullableOptionalNumber(),
      netEmissionsBeef:  NullableOptionalNumber(),
      netEmissionsSheep:  NullableOptionalNumber(),
      netGrainsEmissions:  NullableOptionalNumber(),

      // Feedlot related
      kgCo2PerKgMeatFed: NullableOptionalNumber(),
      kgCo2PerKgMeatFedScope1and2: NullableOptionalNumber(),
      netFeedlotEmissions:  NullableOptionalNumber(),

      // Processor related
      kgCO2ePerTonneHCSWScope1and2:  NullableOptionalNumber(),
      kgCO2ePerTonneHCSW:  NullableOptionalNumber(),
      kgCO2ePerTonneByProductScope1and2:  NullableOptionalNumber(),
      kgCO2ePerTonneByProduct:  NullableOptionalNumber(),
      energyUseMJPerTonneHSCW:  NullableOptionalNumber(),
      netProcessorEmissions:  NullableOptionalNumber(),

      // Dairy related
      kgCo2PerKgMilkFPCM:  NullableOptionalNumber(),
      kgCo2PerKgMilk:  NullableOptionalNumber(),
      kgCo2PerKgDairyMeat:  NullableOptionalNumber(),
      netDairyMilkEmissions:  NullableOptionalNumber(),
      netDairyMeatEmissions:  NullableOptionalNumber(),
  })
})
export type ReportResultsT = Static<typeof ReportResultsTB>

export const CO2_TB = Type.Literal('CO2')
export const N20_TB = Type.Literal('N20')
export const CH4_TB = Type.Literal('CH4')
export const EmissionsGasTB = Type.Union([
    CO2_TB,
    N20_TB,
    CH4_TB
])
export type EmissionsGas = Static<typeof EmissionsGasTB>


export const Scope2TB = Type.Object({
    electricity: Type.Number()
})
export type Scope2 = Static<typeof Scope2TB>

export const Scope1OutputAusTB = Type.Object({
    lime: Type.Number(),
    urea: Type.Number(),
    entericMethaneSheep: Type.Number(),
    entericMethaneBeef: Type.Number(),
    manureMethane: Type.Number(),
    savannahBurningCh4: Type.Number(),
    fertiliser: Type.Number(),
    urineDung: Type.Number(),
    atmosphericDeposition: Type.Number(),
    leachingRunoff: Type.Number(),
    savannahBurningN2O: Type.Number(),
})
export type Scope1OutputAus = Static<typeof Scope1OutputAusTB>

export const Scope1OutputNZTB = Type.Object({
    lime: Type.Number(),
    urea: Type.Number(),
    entericMethaneSheep: Type.Number(),
    entericMethaneBeef: Type.Number(),
    manureMethane: Type.Number(),
    fertiliser: Type.Number(),
    agSoils: Type.Number(),
})
export type Scope1OutputNZ = Static<typeof Scope1OutputNZTB>


export const DairyScope1OutputAusTB = Type.Object({
    lime: Type.Number(),
    urea: Type.Number(),
    entericFermentation: Type.Number(),
    manureManagementCh4: Type.Number(),
    manureManagementN20: Type.Number(),
    animalWaste: Type.Number(),
    fertiliser: Type.Number(),
    urineDung: Type.Number(),
    atmosphericDeposition: Type.Number(),
    leachingRunoff: Type.Number(),
    fuelCo2: Type.Number(),
    transportCo2: Type.Number(),
    fuelCh4: Type.Number(),
    transportCh4: Type.Number(),
    fuelN2O: Type.Number(),
    transportN2O: Type.Number(),
})
export type DairyScope1OutputAusT = Static<typeof DairyScope1OutputAusTB>

export const DairyScope3OutputAusTB = Type.Object({
    fuel: Type.Number(),
    fertiliser: Type.Number(),
    purchasedFeed: Type.Number(),
    pesticides: Type.Number(),
    lime: Type.Number(),
    electricity: Type.Number(),
})
export type DairyScope3OutputAusT = Static<typeof DairyScope3OutputAusTB>


export const DairyAlgorithmOutputTB = Type.Object({
    scope1: DairyScope1OutputAusTB,
    scope2: Scope2TB,
    scope3: DairyScope3OutputAusTB
})
export type DairyAlgorithmOutputT = Static<typeof DairyAlgorithmOutputTB>


export const FeedlotScope1OutputTB = Type.Object({
    lime: Type.Number(),
    urea: Type.Number(),
    entericMethaneBeef: Type.Number(),
    manureMethane: Type.Number(),
    manureMethaneN20Direct: Type.Number(),
    manureMethaneN20InDirect: Type.Number(),
    manureAppliedToSoil: Type.Number(),
    atmosphericDeposition: Type.Number(),
    fuelCo2: Type.Number(),
    transportCo2: Type.Number(),
    fuelCh4: Type.Number(),
    transportCh4: Type.Number(),
    fuelN2O: Type.Number(),
    transportN2O: Type.Number(),
})
export type FeedlotScope1OutputT = Static<typeof FeedlotScope1OutputTB>

export const FeedlotScope3OutputTB = Type.Object({
    fuel: Type.Number(),
    fertiliser: Type.Number(),
    purchasedFeed: Type.Number(),
    pesticides: Type.Number(),
    lime: Type.Number(),
    purchasedLivestock: Type.Number(),
    electricity: Type.Number(),
})
export type FeedlotScope3OutputT = Static<typeof FeedlotScope3OutputTB>


export const FeedlotAlgorithmOutputTB = Type.Object({
    scope1: FeedlotScope1OutputTB,
    scope2: Scope2TB,
    scope3: FeedlotScope3OutputTB
})
export type FeedlotAlgorithmOutputT = Static<typeof FeedlotAlgorithmOutputTB>

export const ProcessorScope1OutputTB = Type.Object({
  fuelCo2: Type.Number(),
  gasCo2: Type.Number(),
  lpgCo2: Type.Number(),
  coalCo2: Type.Number(),
  fuelOilCo2: Type.Number(),
  fuelCh4: Type.Number(),
  methaneCh4: Type.Number(),
  fuelN2O: Type.Number(),
  refrigerantsCo2: Type.Number(),
  wasteWasteCH4: Type.Number(),
  wasteWasteN20: Type.Number(),
})
export type ProcessorScope1OutputT = Static<typeof ProcessorScope1OutputTB>

export const ProcessorScope3OutputTB = Type.Object({
  fuel: Type.Number(),
  purchasedLivestock: Type.Number(),
  electricity: Type.Number(),
  naturalGas: Type.Number(),
  lpg: Type.Number(),
  fuelOil: Type.Number(),
  coal: Type.Number(),
  purchasedGoods: Type.Number(),
})
export type ProcessorScope3OutputT = Static<typeof ProcessorScope3OutputTB>

export const ProcessorAlgorithmOutputTB = Type.Object({
    scope1: ProcessorScope1OutputTB,
    scope2: Scope2TB,
    scope3: ProcessorScope3OutputTB
})
export type ProcessorAlgorithmOutputT = Static<typeof ProcessorAlgorithmOutputTB>

export const  SheepBeefScope3OutputTB = Type.Object({
    fertiliser: Type.Number(),
    purchasedMineralSupplementation: Type.Optional(Type.Number()),
    purchasedFeed: Type.Number(),
    pesticides: Type.Number(),
    lime: Type.Number(),
    purchasedLivestock: Type.Optional(Type.Number()),
    purchasedLivestockSheep: Type.Optional(Type.Number()),
    purchasedLivestockBeef: Type.Optional(Type.Number()),
    livestockOnAgistment: Type.Number(),
})
export type SheepBeefScope3Output = Static<typeof SheepBeefScope3OutputTB>

export const SharedSbGAFScope1TB = Type.Object({
    dieselN2O: Type.Number(),
    petrolN2O: Type.Number(),
    dieselCh4: Type.Number(),
    petrolCh4: Type.Number(),
    dieselCO2: Type.Number(),
    petrolCO2: Type.Number()
})
export type SharedSbGAFScope1 = Static<typeof SharedSbGAFScope1TB>

export const SharedSbGAFScope3TB = Type.Object({
    diesel: Type.Number(),
    petrol: Type.Number(),
    electricity: Type.Number()
})
export type SharedSbGAFScope3 = Static<typeof SharedSbGAFScope3TB>

export const SharedSBGrainsAlgorithmOutputTB = Type.Object({
    scope1: SharedSbGAFScope1TB,
    scope2: Scope2TB,
    scope3: SharedSbGAFScope3TB
})
export type SharedSBGrainsAlgorithmOutput = Static<typeof SharedSBGrainsAlgorithmOutputTB>

export const SbGAFAlgorithmOutputTB = Type.Object({
    scope1: Scope1OutputAusTB,
    scope2: Scope2TB,
    scope3: SheepBeefScope3OutputTB
})
export type SbGAFAlgorithmOutput = Static<typeof SbGAFAlgorithmOutputTB>

export const FullSheepOrBeefResultsTB = Type.Composite([
    Type.Union([SbGAFAlgorithmOutputTB, Scope1OutputNZTB]),
    SharedSBGrainsAlgorithmOutputTB
])
export type FullSheepOrBeefResults = Static<typeof FullSheepOrBeefResultsTB>


export const SheepOrBeefProductSummaryTB = Type.Object({
    totalEmissions: Type.Number(),
    breakdown: FullSheepOrBeefResultsTB
})
export type SheepOrBeefProductSummary = Static<typeof SheepOrBeefProductSummaryTB>

export const CropResultScope1_TB = Type.Object({
    lime: Type.Number(),
    urea: Type.Number(),
    fieldBurningCh4: Type.Number(),
    fertiliser: Type.Number(),
    atmosphericDeposition: Type.Number(),
    fieldBurningN2O: Type.Number(),
    residuesN2O: Type.Number(),
    leachingRunoff: Type.Number(),
})
export type CropResultScope1 = Static<typeof CropResultScope1_TB>

export const CropResultScope3_TB = Type.Object({
    fertiliser: Type.Number(),
    pesticides: Type.Number(),
    lime: Type.Number(),
})
export type CropResultScope3 = Static<typeof CropResultScope3_TB>

export const CropResult_TB = Type.Object({
    scope1: CropResultScope1_TB,
    scope2: Scope2TB,
    scope3: CropResultScope3_TB
})
export type CropResult = Static<typeof CropResult_TB>

export const FullGrainsResults_TB = Type.Composite([
    CropResult_TB,
    SharedSBGrainsAlgorithmOutputTB
])
export type FullGrainsResults = Static<typeof FullGrainsResults_TB>

export const GrainProductSummaryTB = Type.Object({
    cropTypeId: Type.Optional(
      Type.Union(CROP_TYPES.map(ct => Type.Literal(ct.value)))
    ),
    grainRecordStableId: Type.String(),
    totalEmissions: Type.Number(),
    intensity: Type.Number(),
    breakdown: FullGrainsResults_TB
})
export type GrainProductSummary = Static<typeof GrainProductSummaryTB>

export const DairyProductSummaryTB = Type.Object({
    totalEmissions: Type.Number(),
    breakdown: DairyAlgorithmOutputTB
})
export type DairyProductSummaryT = Static<typeof DairyProductSummaryTB>

export const FeedlotBeefProductSummaryTB = Type.Object({
    totalEmissions: Type.Number(),
    breakdown: FeedlotAlgorithmOutputTB
})
export type FeedlotBeefProductSummaryT = Static<typeof FeedlotBeefProductSummaryTB>

export const ProcessorProductSummaryTB = Type.Object({
    totalEmissions: Type.Number(),
    breakdown: ProcessorAlgorithmOutputTB
})
export type ProcessorProductSummaryT = Static<typeof ProcessorProductSummaryTB>

export const AllocatedResultsTB = Type.Object({
    sheep: Type.Optional(SheepOrBeefProductSummaryTB),
    beef: Type.Optional(SheepOrBeefProductSummaryTB),
    grains: Type.Optional(Type.Array(GrainProductSummaryTB)),
    dairyMilk: Type.Optional(DairyProductSummaryTB),
    dairyMeat: Type.Optional(DairyProductSummaryTB),
    feedlotBeef: Type.Optional(FeedlotBeefProductSummaryTB),
    processor: Type.Optional(ProcessorProductSummaryTB)
})

export const VaryingAllocatedResultTB = Type.Union([
  SheepOrBeefProductSummaryTB,
  GrainProductSummaryTB,
  DairyProductSummaryTB,
  FeedlotBeefProductSummaryTB,
  ProcessorProductSummaryTB
])
export type VaryingAllocatedResultT = Static<typeof VaryingAllocatedResultTB>

export const LabelAndGasTB = Type.Object({
    label: Type.String(),
    gas: EmissionsGasTB
})
export type LabelAndGas = Static<typeof LabelAndGasTB>


export const scope1LabelsGasLookup: {
    [Property in (keyof Scope1OutputAus | keyof Scope1OutputNZ | keyof SharedSbGAFScope1 | keyof CropResultScope1 | keyof DairyScope1OutputAusT | keyof FeedlotScope1OutputT | keyof ProcessorScope1OutputT)]: LabelAndGas
} = {
    lime: { label: 'Lime', gas: CO2_TB.const },
    dieselN2O: { label: 'Diesel', gas: N20_TB.const },
    petrolN2O: { label: 'Petrol', gas: N20_TB.const },
    dieselCh4: { label: 'Diesel', gas: CH4_TB.const },
    petrolCh4: { label: 'Petrol', gas: CH4_TB.const },
    dieselCO2: { label: 'Diesel', gas: CO2_TB.const },
    petrolCO2: { label: 'Petrol', gas: CO2_TB.const },
    urea: { label: 'Urea', gas: CO2_TB.const },
    entericMethaneSheep: { label: 'Enteric Methane - Sheep', gas: CH4_TB.const },
    entericMethaneBeef: { label: 'Enteric Methane - Cattle', gas: CH4_TB.const },
    manureMethane: { label: 'Methane in Manure', gas: CH4_TB.const },
    savannahBurningCh4: { label: 'Savannah burning', gas: CH4_TB.const },
    savannahBurningN2O: { label: 'Savannah burning', gas: N20_TB.const },
    fertiliser: { label: 'Fertiliser', gas: N20_TB.const },
    urineDung: { label: 'Urine & dung', gas: N20_TB.const },
    atmosphericDeposition: { label: 'Atmospheric deposition', gas: N20_TB.const },
    leachingRunoff: { label: 'Leaching and runoff', gas: N20_TB.const },

    // NZ
    agSoils: { label: 'Ag Soils', gas: N20_TB.const },

    // Crops
    fieldBurningCh4: { label: 'Field burning', gas: CH4_TB.const },
    fieldBurningN2O: { label: 'Field burning', gas: N20_TB.const },
    residuesN2O: { label: 'Crop residues', gas: N20_TB.const },

    // Dairy
    entericFermentation: { label: 'Enteric Fermentation', gas: CH4_TB.const },
    manureManagementCh4: { label: 'Manure Management', gas: CH4_TB.const },
    manureManagementN20: { label: 'Manure Management', gas: N20_TB.const },
    animalWaste: { label: 'Animal Waste', gas: N20_TB.const },
    transportCo2: { label: 'Transport', gas: CO2_TB.const },
    transportCh4: { label: 'Transport', gas: CH4_TB.const },
    transportN2O: { label: 'Transport', gas: N20_TB.const },
    fuelCo2: { label: 'Fuel', gas: CO2_TB.const },
    fuelCh4: { label: 'Fuel', gas: CH4_TB.const},
    fuelN2O: { label: 'Fuel', gas: N20_TB.const },

    // Feedlot
    manureMethaneN20Direct: { label: 'Manure (Direct)', gas: N20_TB.const },
    manureMethaneN20InDirect: { label: 'Manure (Indirect)', gas: N20_TB.const },
    manureAppliedToSoil: { label: 'Manure applied to soil', gas: N20_TB.const },

    // Processor
    gasCo2: { label: 'Natural Gas', gas: CO2_TB.const },
    lpgCo2: { label: 'LPG', gas: CO2_TB.const },
    coalCo2: { label: 'Coal', gas: CO2_TB.const },
    fuelOilCo2: { label: 'Fuel Oil', gas: CO2_TB.const },
    methaneCh4: { label: 'Methane', gas: CH4_TB.const },
    refrigerantsCo2: { label: 'Refrigerants', gas: CO2_TB.const },
    wasteWasteCH4: { label: 'Waste Water', gas: CH4_TB.const },
    wasteWasteN20: { label: 'Waste Water', gas: N20_TB.const },
}

export const scope2LabelsGasLookup: {
    [Property in (keyof Scope2)]: string
} = {
    electricity: 'Electricity',
}

export const scope3LabelsLookup: {
    [Property in (keyof SheepBeefScope3Output | keyof SharedSbGAFScope3 | keyof CropResultScope3 | keyof DairyScope3OutputAusT | keyof FeedlotScope3OutputT  | keyof ProcessorScope3OutputT)]: string
} = {
    fertiliser: 'Fertiliser',
    purchasedMineralSupplementation: 'Purchased Mineral Supplementation',
    purchasedFeed: 'Purchased Feed',
    pesticides: 'Pesticides',
    lime: 'Lime',
    purchasedLivestock: 'Purchased Livestock',
    purchasedLivestockSheep: 'Purchased Livestock - Sheep',
    purchasedLivestockBeef: 'Purchased Livestock - Cattle',
    livestockOnAgistment: 'Livestock On Agistment',
    diesel: 'Diesel',
    petrol: 'Petrol',
    electricity: 'Electricity',
    fuel: 'Fuel',

    // Processor
    naturalGas: 'Natural Gas',
    lpg: 'LPG',
    fuelOil: 'Fuel Oil',
    coal:'Coal',
    purchasedGoods: 'Purchased Goods',
}
